import { useEffect, useRef, useState, type ReactNode } from 'react'
import { dia, cells, fitContent, onSized, zoomAbout } from '../lib/joint'
import { LineIcon } from './LineIcon'

// The one JointJS canvas: a paper mounted once its host has real dimensions (the blank-canvas race),
// fitted and re-fitted on resize, with zoom controls, a legend slot, hover cards, and click-to-open.
// A map supplies only its `build` (cells onto the model); the interop lives here and in lib/joint.ts.
// Cells that carry a `personId` open on click, cells that carry an `accountId` open the account; cells
// that carry a `title` show it on hover; when the map supplies `hoverCard`, a cell's `hoverId` renders that
// card beside the pointer instead of a native tooltip. The wheel zooms about the cursor and a drag anywhere
// (blank or on a cell) pans (REQ-109: "I can't press on the dots", "the zoomer just scrolls").

const MIN_ZOOM = 0.2; const MAX_ZOOM = 3
/** A press that travels this many pixels or more is a pan, not a click. */
const CLICK_SLOP = 6

export function JointCanvas({ build, deps, onOpen, onOpenAccount, legend, toolbar, className, hoverCard }: {
  build: (model: any, w: number, h: number) => void
  deps: unknown[]
  onOpen?: (id: string) => void
  onOpenAccount?: (companyId: string) => void
  legend?: ReactNode
  toolbar?: ReactNode
  className?: string
  hoverCard?: (id: string) => ReactNode
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(100)
  const [panning, setPanning] = useState(false)
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null)
  const controls = useRef<{ fit: () => void; zoom: (f: number) => void } | null>(null)
  const openRef = useRef(onOpen)
  openRef.current = onOpen
  const accountRef = useRef(onOpenAccount)
  accountRef.current = onOpenAccount
  const cardRef = useRef(hoverCard)
  cardRef.current = hoverCard

  useEffect(() => {
    const host = hostRef.current; const el = paperRef.current
    if (!host || !el) return
    let paper: any = null
    const readout = () => setZoom(Math.round(paper.scale().sx * 100))
    /** Zoom by `factor` about paper pixel (px, py); the +/− buttons use the centre, the wheel the cursor. */
    const zoomAt = (px: number, py: number, factor: number) => {
      const { tx, ty } = paper.translate()
      const z = zoomAbout(paper.scale().sx, tx, ty, px, py, factor, MIN_ZOOM, MAX_ZOOM)
      paper.scale(z.scale); paper.translate(z.tx, z.ty); readout()
    }
    // the wheel zooms at the cursor; registered non-passive so the page does not scroll under the map
    const onWheel = (e: WheelEvent) => {
      if (!paper) return
      e.preventDefault()
      const r = el.getBoundingClientRect()
      zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.1 : 1 / 1.1)
    }
    host.addEventListener('wheel', onWheel, { passive: false })
    const mount = (w: number, h: number) => {
      const model = new dia.Graph({}, { cellNamespace: cells })
      build(model, w, h)
      // a hand on everything that opens (the group, so the label carries it too; scout.Person is styled in CSS);
      // everything else sheds the library's default `move` — nothing here moves — and inherits the paper's grab
      for (const cell of model.getElements()) {
        if (cell.personId || cell.accountId) cell.attr('root/cursor', 'pointer')
        else cell.removeAttr('root/cursor')
      }
      // a line is drawn, never pressed: the library gives every link an invisible 10 px wrapper for hit-testing,
      // and a firm-to-card line crossing another card was swallowing the click on that card (prod, 2026-09-28)
      for (const link of model.getLinks()) { link.attr('wrapper/pointerEvents', 'none'); link.attr('line/pointerEvents', 'none') }
      // interactive: false keeps the elements where the layout put them; clickThreshold lets a click survive
      // the mousemove or two a real hand makes between press and release
      // the paper lives in a node of its own: paper.remove() takes its element out of the document, and when that element was
      // the one React owns, the next build (a filter typed under the map, 2026-10-01) drew into a detached node and the map went blank
      const slot = document.createElement('div')
      el.replaceChildren(slot)
      paper = new dia.Paper({ el: slot, model, width: w, height: h, gridSize: 1, interactive: false, clickThreshold: CLICK_SLOP, background: { color: 'transparent' }, cellViewNamespace: cells })
      // a drag anywhere pans: JointJS delegates the move/up to the document, so the pan follows the pointer
      // off the paper; `travelled` is what separates a pan that ended on a dot from a click on it
      let pan: { x: number; y: number; tx: number; ty: number } | null = null
      let travelled = 0
      const down = (evt: any) => { const t = paper.translate(); pan = { x: evt.clientX, y: evt.clientY, tx: t.tx, ty: t.ty }; travelled = 0 }
      const move = (evt: any) => {
        if (!pan) return
        const dx = evt.clientX - pan.x; const dy = evt.clientY - pan.y
        travelled = Math.max(travelled, Math.hypot(dx, dy))
        if (travelled >= CLICK_SLOP) { setPanning(true); setHover(null) }
        paper.translate(pan.tx + dx, pan.ty + dy)
      }
      const up = () => { pan = null; setPanning(false) }
      paper.on('blank:pointerdown', (evt: any) => { setHover(null); down(evt) })
      paper.on('cell:pointerdown', (_view: any, evt: any) => down(evt))
      paper.on('blank:pointermove', move)
      paper.on('cell:pointermove', (_view: any, evt: any) => move(evt))
      paper.on('blank:pointerup cell:pointerup', up)
      paper.on('element:pointerclick', (view: any) => {
        if (travelled >= CLICK_SLOP) return
        const m = view.model
        if (m.personId) openRef.current?.(m.personId)
        else if (m.accountId) accountRef.current?.(m.accountId)
      })
      paper.on('element:mouseenter', (view: any, evt: any) => {
        const id = view.model.hoverId
        if (id && cardRef.current) {
          const r = host.getBoundingClientRect()
          setHover({ id, x: evt.clientX - r.left, y: evt.clientY - r.top })
          view.el.removeAttribute('title')
        } else { const t = view.model.title; if (t) view.el.setAttribute('title', t) }
      })
      paper.on('element:mouseleave', () => setHover(null))
      fitContent(paper, model, w, h, 28)
      readout()
      controls.current = {
        fit: () => { fitContent(paper, model, w, h, 28); readout() },
        zoom: (f) => { const size = paper.getComputedSize(); zoomAt(size.width / 2, size.height / 2, f) },
      }
    }
    const disconnect = onSized(host, mount, (w, h) => { if (paper) { paper.setDimensions(w, h); fitContent(paper, paper.model, w, h, 28); readout() } })
    return () => { disconnect(); host.removeEventListener('wheel', onWheel); if (paper) paper.remove() }
  }, deps)

  const hostW = hostRef.current?.clientWidth ?? 0
  const cardLeft = hover ? (hover.x > hostW - 300 ? hover.x - 296 : hover.x + 16) : 0
  return (
    <div className={`influence-map ${className ?? ''} ${panning ? 'panning' : ''}`} ref={hostRef}>
      <div className="influence-paper" ref={paperRef} />
      {hover && hoverCard && <div className="hover-card" style={{ left: cardLeft, top: Math.max(8, hover.y - 12) }}>{hoverCard(hover.id)}</div>}
      {legend && <div className="influence-legend">{legend}</div>}
      <div className="influence-controls">
        {toolbar}
        <button className="button small" onClick={() => controls.current?.zoom(1.2)} title="Zoom in">+</button>
        <button className="button small" onClick={() => controls.current?.zoom(1 / 1.2)} title="Zoom out">−</button>
        <button className="button small icon" onClick={() => controls.current?.fit()} title="Fit"><LineIcon name="fit" size={14} /> Fit</button>
        <span className="muted small" title="Wheel zooms at the cursor; drag pans">{zoom}%</span>
      </div>
    </div>
  )
}

/** A design token's current value, for SVG attributes that cannot take var(). */
export function token(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}
